// `/min` is the size-optimised libphonenumber-js build (~35 KB gz) — validation
// only, no formatting helpers we don't use.
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';
import { getCountryName } from './countries';

// Permissive email shape check — mirrors the HTML spec's minimum. We
// intentionally avoid full RFC-5322 here; the ground truth for "does this
// email work" is delivery, not regex.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared between the Create modal and the inline-editable Details card so
// their validation rules can't drift. Pure — no React, no side effects.
//
// Input: the current in-progress customer record (strings, possibly empty).
// Output: { errors: { <field>: string|null }, hasErrors: boolean }.
//
// Country-dependent format checks (phone, postcode) stay silent until a
// country is picked — the "Country is required" error is what drives the
// user to pick one.
export function validateCustomer({
    name,
    contactName,
    country,
    email,
    phone,
    addressLine1,
    townCity,
    postcode,
}) {
    const countryName = country ? getCountryName(country) : '';

    const nameError        = !name?.trim()        ? 'Name is required.'         : null;
    const contactNameError = !contactName?.trim() ? 'Contact name is required.' : null;
    const countryError     = !country             ? 'Country is required.'      : null;

    const emailFilled = !!email?.trim();
    const phoneFilled = !!phone?.trim();

    const emailError = emailFilled && !EMAIL_RE.test(email.trim())
        ? 'Enter a valid email address.'
        : null;

    const phoneError = phoneFilled && country && !isValidPhoneNumber(phone.trim(), country)
        ? `Enter a valid phone number for ${countryName}.`
        : null;

    // Cross-field: at least one of Email/Phone must be provided. This error
    // lives on its own key so the UI can surface it beneath the pair rather
    // than on either field individually.
    const contactMethodError = !emailFilled && !phoneFilled
        ? 'Please provide at least an email or phone number.'
        : null;

    const addressLine1Error = !addressLine1?.trim() ? 'Address Line 1 is required.' : null;
    const townCityError     = !townCity?.trim()     ? 'Town/City is required.'      : null;

    // Countries without a postal system (e.g. Ireland pre-Eircode, some
    // Caribbean nations) — treat any non-empty value as acceptable rather
    // than false-rejecting valid input.
    const postcodeError = !postcode?.trim()
        ? 'Postcode is required.'
        : country && postcodeValidatorExistsForCountry(country) && !postcodeValidator(postcode.trim(), country)
            ? `Enter a valid postcode for ${countryName}.`
            : null;

    const errors = {
        name:          nameError,
        contactName:   contactNameError,
        country:       countryError,
        email:         emailError,
        phone:         phoneError,
        contactMethod: contactMethodError,
        addressLine1:  addressLine1Error,
        townCity:      townCityError,
        postcode:      postcodeError,
    };

    const hasErrors = Object.values(errors).some(Boolean);

    return { errors, hasErrors };
}
